import React, { useState, useContext, useMemo, useCallback, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { Button, Tooltip, Grid, IconButton } from '@mui/material';
import { AlertMessagesContext } from 'react-alert-messages';
import { ReactComponent as DashboardSquareAddIcon } from 'images/dashboard-square-add.svg';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable/';
import EditCategoryDialog from 'components/popups/EditCategoryDialog';
import AddCategoryDialog from 'components/popups/AddCategoryDialog';
import ListDialog from 'components/popups/ListDialog';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { UserContext } from 'Context/UserContext';
import { ShopContext } from 'Context/ShopContext';
import { InventoryContext } from 'Context/InventoryContext';
import { USER_PERMISSIONS } from 'const';
import EditIcon from '@mui/icons-material/Edit';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import ItemService from 'services/ItemService';
import { CommonContext } from 'Context/CommonContext';
import { useTranslation } from 'react-i18next';

function ItemsCategories() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { user } = useContext(UserContext);
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { getProducts, getCategories, categories, setCategories } = useContext(InventoryContext);
  const { getKitchens } = useContext(CommonContext);

  const [categoriesFiltered, setCategoriesFiltered] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isShowCreateFormDialog, setShowCreateFormDialog] = useState(false);
  const [itemsOfSelectedCategory, setItemsOfSelectedCategory] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const handleEditFormDialogClose = () => {
    setSelectedCategory(null);
  };

  const onCategoryCreateSuccess = useCallback(
    (category) => {
      if (category) {
        setCategories((prev) => [...prev, category]);
      } else {
        getCategories({ useCache: false });
      }
    },
    [getCategories, setCategories]
  );

  const onCategoryUpdateSuccess = useCallback(
    (updatedCateg) => {
      const cat = updatedCateg?.data ?? updatedCateg;
      if (cat && cat.id) {
        const newCategories = categories.map((c) => (c.id === cat.id ? cat : c));
        setCategories(newCategories);
        setCategoriesFiltered(newCategories);
      } else {
        getCategories({ useCache: false }).then((fetched) => {
          setCategoriesFiltered(fetched);
        });
      }
    },
    [getCategories, setCategories, categories]
  );

  const handleCreateFormDialogClose = () => {
    setShowCreateFormDialog(false);
  };

  const onCategorySelected = (category) => {
    setSelectedCategory(category);
  };

  const onCloseItemsDialog = () => {
    setItemsOfSelectedCategory(null);
  };

  const handleOpenCreateFormDialog = () => {
    setShowCreateFormDialog(true);
  };

  const handleDeleteCategory = useCallback(
    async (categoryId) => {
      setLoadingIndicator(true);
      try {
        await ItemService.deleteItemCategory(categoryId);
        setCategories((prev) => prev.filter((c) => c.id !== categoryId));
        postAlertMessage({ text: tManagement('Category deleted successfully'), type: 'success' });
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    },
    [postAlertMessage, setCategories, tManagement]
  );

  const handleCategorySearch = (value) => {
    setSearchText(value);
    setCategoriesFiltered(categories.filter((category) => category.name.toLowerCase().includes(value.toLowerCase())));
  };

  const getItemsWithLoadingIndicator = useCallback(async () => {
    setLoadingIndicator(true);
    try {
      const allProducts = await getProducts();
      return allProducts;
    } finally {
      setLoadingIndicator(false);
    }
  }, [getProducts]);

  const getCategoryItems = useCallback(
    async (category) => {
      const items = await getItemsWithLoadingIndicator();
      const itemsOfCategory = items.filter((item) => item.category === category.id);
      setItemsOfSelectedCategory(itemsOfCategory);
    },
    [getItemsWithLoadingIndicator, setItemsOfSelectedCategory]
  );

  const headerData = useMemo(() => {
    let headers = [
      {
        label: tCommon('Name'),
        id: 'name',
        type: 'text',
      },
    ];

    if (shop && shop.image_support_validity != null) {
      headers.splice(1, 0, {
        label: tCommon('Image'),
        id: 'image',
        type: 'image',
      });
    }
    headers.push(
      {
        label: tCommon('Available'),
        id: 'is_available',
        type: 'callback',
        viewRender: (category) => (category.is_available ? 'Available' : 'Unavailable'),
      },
      {
        label: tCommon('Available On Cart'),
        id: 'is_available_on_cart',
        type: 'callback',
        viewRender: (category) => (category.is_available_on_cart ? 'Available' : 'Unavailable'),
      },
      {
        label: tCommon('Order'),
        id: 'order',
        type: 'text',
      }
    );

    if (shop && (shop.kot_validity != null || shop.kot_lite_validity != null)) {
      headers.push(
        {
          label: tCommon('Kitchen Ip'),
          id: 'kitchen_ip',
          type: 'text',
        },
        {
          label: tCommon('Kitchen Name'),
          id: 'kitchen_name',
          type: 'text',
        },
        {
          label: tCommon('Kitchen'),
          id: 'kitchen',
          type: 'callback',
          viewRender: (category) => category.kitchen?.name || '-',
        }
      );
    }
    headers.push({
      label: tCommon('Actions'),
      id: 'viewItems',
      title: 'View Products',
      type: 'callback',
      viewRender: (category) => {
        return (
          <Grid className={styles.btnGroup}>
            <Tooltip title={tCommon('View Items')}>
              <IconButton aria-label={tCommon('View Items')} size="small" onClick={() => getCategoryItems(category)}>
                <RemoveRedEyeIcon className={styles.iconButton} />
              </IconButton>
            </Tooltip>
            {((user || {}).shop_permissions || {}).products_permission === USER_PERMISSIONS.WRITE && (
              <>
                <Tooltip title={tManagement('Edit Category')}>
                  <IconButton
                    aria-label={tManagement('Edit Category')}
                    size="small"
                    onClick={() => onCategorySelected(category)}
                  >
                    <EditIcon className={styles.iconButton} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={tManagement('Delete Category')}>
                  <IconButton
                    aria-label={tManagement('Delete Category')}
                    size="small"
                    onClick={() => {
                      setCategoryToDelete(category.id);
                      setShowDeleteConfirm(true);
                    }}
                  >
                    <DeleteIcon className={styles.iconButton} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Grid>
        );
      },
    });
    return headers;
  }, [shop, user, getCategoryItems, tCommon, tManagement]);

  const listHeader = [
    {
      label: tCommon('PLU'),
      id: 'plu',
      type: 'text',
    },
    {
      label: tCommon('Item Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Price'),
      id: 'price',
      type: 'text',
    },
  ];

  useEffect(() => {
    if (categories) {
      setCategoriesFiltered(categories);
      return;
    }

    let mounted = true;
    (async () => {
      const fetched = await getCategories();
      if (!mounted) return;
      setCategoriesFiltered(fetched);
    })();

    return () => {
      mounted = false;
    };
  }, [getCategories, categories]);

  useEffect(() => {
    getKitchens();
  }, [getKitchens]);

  return (
    <div className={styles.contentWrapperSm}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>{tCommon('Categories')}</h2>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}></div>
          <div className={styles.filerInputSec}>
            {((user || {}).shop_permissions || {}).products_permission === USER_PERMISSIONS.READ ? (
              ''
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={styles.actionBtn}
                style={{ backgroundColor: '#ff851b' }}
                onClick={handleOpenCreateFormDialog}
              >
                <DashboardSquareAddIcon className={styles.actionBtnSvg} />
                {tManagement('New category')}
              </Button>
            )}
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                placeholder={tManagement('Search category')}
                onChange={(e) => {
                  handleCategorySearch(e.target.value);
                }}
                className={styles.searchInput}
              />
              <Tooltip title={tCommon('Search')} placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
      {(categories !== null || loadingIndicator || categories === null) &&
        ((categoriesFiltered && categoriesFiltered.length > 0) || loadingIndicator || categories === null ? (
          <DataTable
            columns={headerData}
            rows={categoriesFiltered || []}
            isLoading={loadingIndicator || categories === null}
          />
        ) : (
          !loadingIndicator &&
          categories !== null && (
            <Info
              title={tManagement('You have no category to list')}
              content={tManagement("You haven't categories with the given properties")}
            />
          )
        ))}
      {selectedCategory && (
        <EditCategoryDialog
          category={selectedCategory}
          handleClose={handleEditFormDialogClose}
          onSuccess={onCategoryUpdateSuccess}
        />
      )}
      {isShowCreateFormDialog && (
        <AddCategoryDialog
          handleClose={handleCreateFormDialogClose}
          onSuccess={onCategoryCreateSuccess}
        />
      )}
      {itemsOfSelectedCategory && (
        <ListDialog
          onClose={onCloseItemsDialog}
          rowData={itemsOfSelectedCategory}
          tableHeader={listHeader}
          title={tManagement('Category Products')}
          emptyDataTitle={tManagement('You have an empty list of items for this category!')}
          emptyDataContent={tManagement('Start adding your items for this category by editing this category')}
        />
      )}
      {showDeleteConfirm && (
        <ConfirmPopup
          data={showDeleteConfirm}
          alertTitle={tManagement('Delete Category')}
          alertContent={tManagement('Are you sure you want to delete this category?')}
          handleClose={() => {
            setShowDeleteConfirm(false);
            setCategoryToDelete(null);
          }}
          handleOkay={async () => {
            if (categoryToDelete) {
              await handleDeleteCategory(categoryToDelete);
              setCategoryToDelete(null);
              setShowDeleteConfirm(false);
            }
          }}
          successBtnName="Delete"
        />
      )}
    </div>
  );
}

export default withConsoleBase(ItemsCategories);
